// SPDX-License-Identifier: GPL-3.0-or-later
import { parseMultikeyManifest, type MultikeyOccurrence } from "./multikey";
import { MULTIKEY_MAX_INPUT_BYTES, type MultikeyPageGeometry } from "./multikey-worker-protocol";

export const MULTIKEY_MAX_INDEX_BYTES = 4 * 1024 * 1024;

/** Exact published source identity; filenames never override the configured ordinary URL. */
export interface MultikeyFileIdentity {
  readonly file: string;
  readonly byteLength: number;
  readonly identity: string;
}
/** Inclusive global range endpoints, with a sibling URL resolved from the advertised JSON. */
export interface MultikeyRange extends MultikeyFileIdentity {
  readonly firstPage: number;
  readonly lastPage: number;
  readonly pageCount: number;
  readonly url: string;
}
/** Sole global musical/geometry authority, independent of range-local PDF object identities. */
export interface MultikeyIndex {
  readonly url: string;
  readonly canonical: MultikeyFileIdentity;
  readonly pageCount: number;
  readonly geometry: readonly MultikeyPageGeometry[];
  readonly occurrences: readonly MultikeyOccurrence[];
  readonly ranges: readonly MultikeyRange[];
}

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const integer = (value: unknown, max: number): value is number =>
  Number.isSafeInteger(value) && (value as number) > 0 && (value as number) <= max;
const sibling = (value: unknown, extension: string): value is string =>
  typeof value === "string" &&
  new TextEncoder().encode(value).byteLength <= 255 &&
  value.trim() === value &&
  !value.startsWith(".") &&
  !/[\\/%:?#\u0000-\u001f\u007f]/.test(value) &&
  !value.includes("..") &&
  value.toLowerCase().endsWith(extension);
const invalid = (): never => {
  throw new Error("Invalid or unsupported multikey range index");
};
function identity(value: unknown): MultikeyFileIdentity {
  if (
    !record(value) ||
    !sibling(value.file, ".pdf") ||
    !integer(value.byte_length, MULTIKEY_MAX_INPUT_BYTES) ||
    typeof value.sha256 !== "string" ||
    !/^[a-f0-9]{64}$/.test(value.sha256)
  )
    return invalid();
  return Object.freeze({ file: value.file, byteLength: value.byte_length, identity: value.sha256 });
}

/** Rejects malformed, ambiguous and unsafe packages before any range download or PDF processing. */
export function parseMultikeyIndex(raw: string, indexUrl: string): MultikeyIndex {
  if (new TextEncoder().encode(raw).byteLength > MULTIKEY_MAX_INDEX_BYTES) return invalid();
  const value: unknown = JSON.parse(raw);
  const url = new URL(indexUrl);
  if (
    !["http:", "https:"].includes(url.protocol) ||
    url.username ||
    url.password ||
    !record(value) ||
    value.format !== "ulsbs:multikey-ranges" ||
    value.schema_version !== 1 ||
    value.link_policy !== "canonical-names-v1" ||
    !integer(value.page_count, 10_000) ||
    !integer(value.max_pages_per_range, 10_000) ||
    !integer(value.target_max_range_bytes, MULTIKEY_MAX_INPUT_BYTES) ||
    !record(value.packaging) ||
    value.packaging.tool !== "qpdf" ||
    typeof value.packaging.tool_version !== "string" ||
    !/^\d+\.\d+\.\d+$/.test(value.packaging.tool_version) ||
    !Array.isArray(value.geometry) ||
    value.geometry.length !== value.page_count ||
    !Array.isArray(value.ranges) ||
    !value.ranges.length ||
    value.ranges.length > value.page_count
  )
    return invalid();
  const canonical = identity(value.canonical);
  const geometry: MultikeyPageGeometry[] = value.geometry.map(g => {
    if (
      !record(g) ||
      !Array.isArray(g.view) ||
      g.view.length !== 4 ||
      !g.view.every(v => typeof v === "number" && Number.isFinite(v)) ||
      g.view[2] <= g.view[0] ||
      g.view[3] <= g.view[1] ||
      ![0, 90, 180, 270].includes(g.rotation as number) ||
      typeof g.user_unit !== "number" ||
      !Number.isFinite(g.user_unit) ||
      g.user_unit <= 0
    )
      return invalid();
    return Object.freeze({
      view: Object.freeze([...g.view]) as MultikeyPageGeometry["view"],
      rotation: g.rotation as number,
      userUnit: g.user_unit,
    });
  });
  const occurrences = parseMultikeyManifest(JSON.stringify(value.manifest), value.page_count);
  if (occurrences === null) return invalid();
  // Finalized anchors must be coordinates in their canonical page's visible PDF box.
  for (const occurrence of occurrences) {
    if (occurrence.anchor && occurrence.pageIndex !== null) {
      const { view } = geometry[occurrence.pageIndex]!;
      if (
        occurrence.anchor.x < view[0] ||
        occurrence.anchor.x > view[2] ||
        occurrence.anchor.y < view[1] ||
        occurrence.anchor.y > view[3]
      )
        return invalid();
    }
  }
  let nextPage = 1;
  const files = new Set<string>();
  const ranges = value.ranges.map(r => {
    const file = identity(r);
    if (
      !record(r) ||
      !integer(r.first_page, value.page_count as number) ||
      !integer(r.last_page, value.page_count as number) ||
      r.first_page !== nextPage ||
      r.last_page < r.first_page ||
      r.page_count !== r.last_page - r.first_page + 1 ||
      (r.page_count as number) > (value.max_pages_per_range as number) ||
      files.has(file.file) ||
      file.file === canonical.file
    )
      return invalid();
    nextPage = r.last_page + 1;
    files.add(file.file);
    return Object.freeze({
      ...file,
      firstPage: r.first_page,
      lastPage: r.last_page,
      pageCount: r.page_count as number,
      url: new URL(encodeURIComponent(file.file), url).href,
    });
  });
  if (nextPage !== value.page_count + 1) return invalid();
  return Object.freeze({
    url: url.href,
    canonical,
    pageCount: value.page_count,
    geometry: Object.freeze(geometry),
    occurrences,
    ranges: Object.freeze(ranges),
  });
}

/** Resolves an advertised global logical page without guessing producer batch sizes. */
export function multikeyRangeForPage(index: MultikeyIndex, page: number): MultikeyRange {
  const range = index.ranges.find(r => page >= r.firstPage && page <= r.lastPage);
  if (!Number.isSafeInteger(page) || !range)
    throw new Error("Multikey page outside source coverage");
  return range;
}
